<template>
  <div>
    <default-dialog :loading="loading" fullscreen>
      <template slot="title">
        {{ supplierData?.name }}
      </template>
      <template slot="content">
        <v-container>
          <v-expansion-panels popout v-model="panel" flat active-class="elevation-4" class="mt-3" v-if="!loading">
            
            <!-- Informações Básicas -->
            <v-expansion-panel style="border-bottom: solid #ddd 1px;" ripple>
              <v-expansion-panel-header class="red--text font-weight-bold">
                <div>
                  <v-icon size="19" color="red">mdi-information</v-icon>
                  Informações básicas
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <card-loading-transparent v-if="saving" loadingText="Salvando"></card-loading-transparent>
                <v-form v-if="!saving" ref="basicInfoForm">
                  <div class="d-flex">
                    <v-spacer></v-spacer>
                    <f-btn :disabled="!userAllowed" class="ml-2" @click="startEditing('basic')" v-if="!editingStates.basic" edit></f-btn>
                    <f-btn v-if="editingStates.basic" class="ml-2" @click="cancelEditing('basic')" cancel></f-btn>
                    <f-btn v-if="editingStates.basic" color="success" small class="ml-2" @click="saveBasicInfo" :disabled="!isNameValid" save></f-btn>
                  </div>
                  <v-row>
                    <v-col cols='12' sm='6' md='6'>
                      <v-text-field 
                        v-model="supplierData.name" 
                        label="Nome Simplificado" 
                        :rules="required"
                        :disabled="!editingStates.basic"
                      ></v-text-field>
                      </v-col>
                    <v-col cols='12' sm='6' md='6'>
                      <v-text-field 
                        v-model="supplierData.fantasyName" 
                        label="Nome Fantasia" 
                        :rules="required"
                        :disabled="!editingStates.basic"
                      ></v-text-field>
                    </v-col>
                    <v-col cols='12' sm='6' md='6'>
                      <v-text-field 
                        v-model="supplierData.CNPJ" 
                        label="CNPJ" 
                        v-mask="'##.###.###/####-##'" 
                        :rules="required"
                        :disabled="!editingStates.basic"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-alert type="warning" color="orange" v-if="!isNameValid"> Nome já cadastrado.</v-alert>
                </v-form>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Endereço -->
            <v-expansion-panel style="border-bottom: solid #ddd 1px;" ripple>
              <v-expansion-panel-header class="red--text font-weight-bold">
                <div>
                  <v-icon size="19" color="red">mdi-map</v-icon>
                  Endereço
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content v-if="!loading">
                <card-loading-transparent v-if="saving" loadingText="Salvando"></card-loading-transparent>
                <div v-if="!saving">
                  <div class="d-flex">
                    <v-spacer></v-spacer>
                    <f-btn :disabled="!userAllowed" class="ml-2" @click="startEditing('address')" v-if="!editingStates.address" edit></f-btn>
                    <f-btn v-if="editingStates.address" class="ml-2" @click="cancelEditing('address')" cancel></f-btn>
                    <f-btn v-if="editingStates.address" class="ml-2" @click="saveAddress" save></f-btn>
                  </div>
                  <AddressForm 
                    ref="addressForm" 
                    :read_only="!editingStates.address" 
                    :address="supplierData.address"
                  ></AddressForm>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Produtos -->
            <v-expansion-panel style="border-bottom: solid #ddd 1px;" ripple>
              <v-expansion-panel-header class="red--text font-weight-bold">
                <div>
                  <v-icon size="19" color="red">mdi-package-check</v-icon>
                  Produtos vendidos
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content v-if="!loading">
                <v-card flat>
                  <v-card-title class="d-flex align-center justify-space-between">
                    <v-select
                      v-model="statusFilter"
                      :items="statusFilterOptions"
                      label="Filtrar por status"
                      dense
                      outlined
                      style="max-width: 200px;"
                      @change="filterProducts"
                    ></v-select>
                    <v-btn v-if="userAllowed" class="ml-2" @click="openAddProductDialog" color="success">Adicionar</v-btn>
                  </v-card-title>
                  <v-card-text>
                    <v-data-table
                      :headers="productHeaders"
                      :items="filteredProductPickupLocations"
                      :loading="saving"
                      class="elevation-0"
                      no-data-text="Nenhum produto cadastrado"
                    >
                      <template v-slot:item.status_name="{ item }">
                        <v-chip
                          :color="item.status_id === 1 ? 'success' : 'error'"
                          small
                          text-color="white"
                        >
                          {{ item.status_id === 1 ? 'Ativado' : 'Desabilitado' }}
                        </v-chip>
                      </template>
                      <template v-slot:item.actions="{ item }">
                        <v-btn
                          small
                          :color="item.status_id === 1 ? 'error' : 'success'"
                          @click="updateProductStatus(item)"
                          :disabled="!userAllowed"
                        >
                          {{ item.status_id === 1 ? 'Desabilitar' : 'Ativar' }}
                        </v-btn>
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-container>

        <!-- Dialog de Adição de Produtos -->
        <v-dialog 
          :value="showAddProductDialog" 
          scrollable 
          persistent 
          :overlay="true" 
          max-width="600px"
          transition="dialog-transition" 
        >
          <v-card>
            <v-card-title>
              <span class="headline">Adicionar produtos ao local</span>
            </v-card-title>
            <v-card-text>
              <card-loading-transparent v-if="loadingProducts"></card-loading-transparent>
              <v-container grid-list-lg v-if="!loadingProducts">
                <div v-if="availableProducts.length === 0" class="text-center pa-4">
                  <p>Nenhum produto disponível para adicionar</p>
                </div>
                <v-row v-for="(product, index) in availableProducts" :key="`product-${index}`" v-else>
                    <v-col cols='12' sm='12'>
                    <v-checkbox 
                      :label="product.product_name" 
                      v-model="product.selected" 
                      hide-details 
                      class="pa-0"
                    ></v-checkbox>
                    </v-col>
                  </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="error" text @click="closeAddProductDialog"  :disabled="saving">Cancelar</v-btn>
              <v-btn color="success" @click="addProducts" :disabled="saving">Salvar</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

      </template>
      <template slot="actions">
        <f-btn @click="closeDialog" close></f-btn>
      </template>
    </default-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import getters from "@/_backoffice/_registrations/mixins/getters.js"
import validations from "@/mixins/validations";
import Api from '@/api/index';

export default {
  name: 'EditPickupLocation',
  mixins: [getters, validations],
  components: {
    AddressForm: () => import("@/_backoffice/_registrations/components/shared/forms/AddressForm.vue"),
  },
  data() {
    return {
      // Estados de UI
      panel: [],
      loading: true,
      saving: false,
      loadingProducts: false,
      showAddProductDialog: false,
      
      // Estados de edição
      editingStates: {
        basic: false,
        address: false,
      },
      
      // Dados principais
      supplierData: {
        name: '',
        fantasyName: '',
        CNPJ: '',
        brand_id: null,
        address: {},
      },
      
      // Validações
      isNameValid: true,
      nameChanged: false,
      
      // Produtos
      availableProducts: [],
      productPickupLocations: [],
      filteredProductPickupLocations: [],
      
      // Filtro de status
      statusFilter: 'active', // Padrão: apenas ativos
      statusFilterOptions: [
        { text: 'Apenas Ativos', value: 'active' },
        { text: 'Apenas Desabilitados', value: 'inactive' },
        { text: 'Todos', value: 'all' }
      ],
      
      // Headers da tabela de produtos
      productHeaders: [
        { text: 'Nome do Produto', value: 'product_name', sortable: true },
        { text: 'Status', value: 'status_name', sortable: true, align: 'center' },
        { text: 'Ações', value: 'actions', sortable: false, align: 'center' },
      ],
    }
  },
  
  computed: {
    userAllowed() {
      return ['Manager', 'Director', 'SalesExecutive'].includes(
        this.profile?.included[0]?.attributes?.role?.data[0]?.attributes?.name
      );
    },
    
    ...mapState({
      profile: state => state.User.oneUser,
      all_brands: state => state.Supplier.all_brands,
    })
  },
  
  watch: {
    panel(newPanel) {
      // Panel 2 é o de produtos vendidos (índice 2)
      // Verificar se newPanel é um array e se contém o índice 2
      if (Array.isArray(newPanel) && newPanel.includes(2)) {
        this.loadProductPickupLocations();
      } else if (newPanel === 2) {
        // Caso newPanel seja um número (índice único)
        this.loadProductPickupLocations();
      }
    }
  },
  
  methods: {
    // === MÉTODOS DE CARREGAMENTO ===
    async loadData() {
      this.loading = true;
      try {
        const supplier = await this.fetchSupplier();
        this.supplierData = this.extractSupplierData(supplier);
        // Não carregar produtos iniciais - só quando o panel for aberto
        this.productPickupLocations = [];
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        this.addNotification({ type: 'error', msg: 'Erro ao carregar dados' });
      } finally {
        this.loading = false;
      }
    },
    
    async loadProductPickupLocations() {
      try {
        this.saving = true;
        
        // Fetch product_pickup_locations da API v2
        const productPickupLocations = await this.fetchProductPickupLocations();
        
        // Atualizar a lista de produtos vendidos
        this.productPickupLocations = this.extractProductPickupLocationsFromResponse(productPickupLocations);
        
        // Aplicar filtro padrão (apenas ativos)
        this.filterProducts();
        
      } catch (error) {
        console.error('Erro ao carregar produtos vendidos:', error);
        this.addNotification({ type: 'error', msg: 'Erro ao carregar produtos vendidos' });
      } finally {
        this.saving = false;
      }
    },
    
    async fetchSupplier() {
      const response = await Api.Supplier.show({ 
        id: this.$route.params.id, 
        type: 'pickup_locations' 
      });
      return response.data;
    },
    
    extractSupplierData(supplier) {
      const attrs = supplier.data?.attributes || {};
      return {
        name: attrs.name || '',
        fantasyName: attrs.fantasyName || '',
        CNPJ: attrs.CNPJ || '',
        brand_id: attrs.brand_id,
        address: attrs.address?.data?.attributes || {},
      };
    },
    
    extractProductPickupLocationsFromResponse(productPickupLocations) {
      // Processar a resposta da API v2 de product_pickup_locations
      if (Array.isArray(productPickupLocations)) {
        return productPickupLocations.map(ppl => ({
          id: ppl.id,
          product_name: ppl.product_name || ppl.product?.name,
          product_id: ppl.product_id,
          status_id: ppl.status_id,
          status_name: ppl.status_name,
        }));
      }
      return [];
    },
    
    filterProducts() {
      if (this.statusFilter === 'active') {
        this.filteredProductPickupLocations = this.productPickupLocations.filter(item => item.status_id === 1);
      } else if (this.statusFilter === 'inactive') {
        this.filteredProductPickupLocations = this.productPickupLocations.filter(item => item.status_id !== 1);
      } else {
        this.filteredProductPickupLocations = [...this.productPickupLocations];
      }
    },
    
    // === MÉTODOS DE EDIÇÃO ===
    startEditing(type) {
      this.editingStates[type] = true;
    },
    
    cancelEditing(type) {
      this.editingStates[type] = false;
      // Só recarrega se realmente necessário (dados podem ter sido alterados)
      if (type === 'basic' || type === 'address') {
        this.loadData();
      }
    },
    
    // === MÉTODOS DE SALVAMENTO ===
    async saveBasicInfo() {
      if (!this.$refs.basicInfoForm.validate()) return;
      
      try {
        this.saving = true;
        const data = {
          id: this.$route.params.id,
          brand_id: this.supplierData.brand_id,
          name: this.supplierData.name,
          fantasyName: this.supplierData.fantasyName,
          CNPJ: this.supplierData.CNPJ,
          status_id: 1
        };
        const response = await Api.Supplier.updatePLInfo(data);
        
        // Atualizar dados localmente com a resposta do servidor
        this.updateSupplierDataFromResponse(response.data);
        
        this.addNotification({ type: 'success', msg: 'Informações básicas atualizadas com sucesso' });
        this.editingStates.basic = false;
      } catch (error) {
        console.error('Erro ao salvar:', error);
        this.addNotification({ type: 'error', msg: 'Erro ao salvar informações' });
      } finally {
        this.saving = false;
      }
    },
    
    async saveAddress() {
      try {
        this.saving = true;
        const data = {
          id: this.$route.params.id,
          address_attributes: {
            id: this.supplierData.address.id || null,
            addr: this.supplierData.address.addr,
            city: this.supplierData.address.city,
            complement: this.supplierData.address.complement,
            neighborhood: this.supplierData.address.neighborhood,
            number: this.supplierData.address.number,
            state: this.supplierData.address.state,
            zip: this.supplierData.address.zip,
          }
        };
        const response = await Api.Supplier.updatePLAddress(data);
        
        // Atualizar dados localmente com a resposta do servidor
        this.updateSupplierDataFromResponse(response.data);
        
        this.addNotification({ type: 'success', msg: 'Endereço atualizado com sucesso' });
        this.editingStates.address = false;
      } catch (error) {
        console.error('Erro ao salvar endereço:', error);
        this.addNotification({ type: 'error', msg: 'Erro ao salvar endereço' });
      } finally {
        this.saving = false;
      }
    },
    
    
    // === MÉTODOS DE ATUALIZAÇÃO LOCAL ===
    updateSupplierDataFromResponse(responseData) {
      const attrs = responseData.data?.attributes || {};
      
      // Atualizar dados básicos
      this.supplierData.name = attrs.name || this.supplierData.name;
      this.supplierData.fantasyName = attrs.fantasyName || this.supplierData.fantasyName;
      this.supplierData.CNPJ = attrs.CNPJ || this.supplierData.CNPJ;
      this.supplierData.brand_id = attrs.brand_id || this.supplierData.brand_id;
      
      // Atualizar endereço se presente na resposta
      if (attrs.address?.data?.attributes) {
        this.supplierData.address = { ...this.supplierData.address, ...attrs.address.data.attributes };
      }
      
      // Marcar que houve mudança para controle de navegação
      this.nameChanged = true;
    },
    
    
    // === MÉTODOS DE PRODUTOS ===
    async openAddProductDialog() {
      this.showAddProductDialog = true;
      this.loadingProducts = true;
      
      try {
        // Fetch product_pickup_locations da API v2
        await this.fetchProductPickupLocations();
        
        // Fetch produtos disponíveis
        const products = await this.fetchProducts();
        this.availableProducts = this.prepareAvailableProducts(products);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        this.addNotification({ type: 'error', msg: 'Erro ao carregar dados' });
      } finally {
        this.loadingProducts = false;
      }
    },
    
    async fetchProductPickupLocations() {
      const response = await Api.SupplierV2.index('product_pickup_locations', { 
        pickup_location_id: this.$route.params.id 
      });
      return response.data;
    },
    
    async fetchProducts() {
      const response = await Api.SupplierV2.index('products', { 
        brand_id: this.supplierData.brand_id,
        without_ppl: true
      });
      return response.data;
    },
    
    prepareAvailableProducts(products) {
      const existingProductIds = this.productPickupLocations.map(p => p.product_id);
      
      // A API v2 retorna um array direto, não dentro de .data
      const productsArray = Array.isArray(products) ? products : (products.data || []);
      return productsArray
      .filter(product => !existingProductIds.includes(product.id))
      .map(product => ({
        product_id: product.id,
        product_name: product.name,
        selected: false
      }));
    },
    
    closeAddProductDialog() {
      this.showAddProductDialog = false;
      this.availableProducts = [];
    },
    
    async addProducts() {
      try {
        this.saving = true;
        const selectedProducts = this.availableProducts.filter(p => p.selected);
        
        if (selectedProducts.length === 0) {
          this.addNotification({ type: 'warning', msg: 'Selecione pelo menos um produto para adicionar' });
          return;
        }

        await Api.SupplierV2.update('pickup_locations', {
          id: this.$route.params.id,
          product_pickup_locations_attributes: selectedProducts.map(p => ({
            product_id: p.product_id,
            pickup_location_id: this.$route.params.id,
            status_id: 1
          }))
        });

        this.addNotification({ 
          type: 'success', 
          msg: `${selectedProducts.length} produto(s) adicionado(s) com sucesso` 
        });

        // Marcar que houve mudança para atualizar a lista ao retornar
        this.nameChanged = true;

        // Recarregar produtos vendidos
        await this.loadProductPickupLocations();
        this.closeAddProductDialog();
      } catch (error) {
        console.error('Erro ao adicionar produtos:', error);
        this.addNotification({ type: 'error', msg: 'Erro ao adicionar produtos' });
      } finally {
        this.saving = false;
      }
    },
    
    async updateProductStatus(product) {
      try {
        this.saving = true;
        const data = {
          id: this.$route.params.id,
          product_pickup_locations_attributes: [{
            id: product.id,
            status_id: product.status_id === 1 ? 2 : 1
          }]
        };
        
        const response = await Api.Supplier.updatePLProducts(data);
        
        // Recarregar produtos vendidos para manter todos os produtos (ativos e desabilitados)
        await this.loadProductPickupLocations();
        
        // Marcar que houve mudança para atualizar a lista ao retornar
        this.nameChanged = true;

        // Se o produto foi desabilitado e o filtro está em "apenas ativos", mudar para "todos"
        if (product.status_id === 1 && this.statusFilter === 'active') {
          this.statusFilter = 'all';
          this.filterProducts();
          this.addNotification({ 
            type: 'info', 
            msg: 'Produto desabilitado. Filtro alterado para "Todos" para visualizar o produto.' 
          });
        } else {
          this.addNotification({ type: 'success', msg: 'Status do produto atualizado com sucesso' });
        }
      } catch (error) {
        console.error('Erro ao atualizar status:', error);
        this.addNotification({ type: 'error', msg: 'Erro ao atualizar status do produto' });
      } finally {
        this.saving = false;
      }
    },
    
 

    
    // === MÉTODOS DE NAVEGAÇÃO ===
    closeDialog() {
      this.$router.push({ 
        name: 'BrandsList', 
        params: { get_list: this.nameChanged } 
      });
    },
  },
  
  mounted() {
    this.loadData();
  },
}
</script>

<style scoped>
</style>